// lib
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  ClickAwayListener,
  ListItemIcon,
  MenuList,
  MenuItem,
  Paper,
  Popper,
  TextField,
  Tooltip,
} from "@material-ui/core";
import { BookmarkBorder, DeleteOutline, Share } from "@material-ui/icons";
// src
import config from "config";
import { PresetSection, PresetType, ResourceType } from "config/constants";
import CourseListContents from "components/plannings/micro/CourseListContents";

const shareUrl = (preset) => preset.share || `${config.root}/light/presets/cs/${preset.id}`;

const PresetMenu = ({
  // from parent
  anchorEl,
  onClose,
  open,
  preset,
  section,
  // data props
  isLogged,
  // dispatch props
  manageAddToFavorites,
  manageRemoveFromFavoritesWithId,
  manageRemoveFromLocalsWithId,
}) => {
  const shareRef = useRef(null);
  const [showShare, setShowShare] = useState(false);
  const [isSharedTooltipOpen, setIsSharedTooltipOpen] = useState(false);
  const { t } = useTranslation();
  useEffect(() => setShowShare(false), [open]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.keyCode === 27) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  if (!preset) return null;
  const { refName } = preset;
  const course =
    preset.resource && preset.resource.type === ResourceType.COURSE ? preset.resource : null;

  const toggleOpenShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(shareUrl(preset)).then(() => {
        setIsSharedTooltipOpen(true);
        setTimeout(() => setIsSharedTooltipOpen(false), 1500);
      });
    }
    setShowShare(!showShare);
    // after event managing is dealt with
    setTimeout(() => shareRef.current.select(), 0);
  };
  const handleAddToFavorites = () => {
    onClose();
    manageAddToFavorites(preset);
  };
  const handleRemoveFromFavorites = () => {
    onClose();
    manageRemoveFromFavoritesWithId(preset.id);
  };
  const handleRemoveFromLocals = () => {
    onClose();
    manageRemoveFromLocalsWithId(preset.id);
  };
  const handleShareTextClick = () => {
    shareRef.current.select();
  };

  return (
    <Popper anchorEl={anchorEl} open={open} placement="right" hidebackdrop="true">
      <ClickAwayListener onClickAway={onClose}>
        <Paper elevation={5} className="preset-menu">
          <MenuList dense>
            <div className="preset-actions">
              {preset.type !== PresetType.INSTANT && (
                <>
                  <Tooltip
                    className="preset-share"
                    open={isSharedTooltipOpen}
                    title="Copié"
                    placement="left"
                    arrow
                  >
                    <TextField
                      inputRef={shareRef}
                      className={showShare ? "show" : "hide"}
                      value={shareUrl(preset)}
                      onClick={handleShareTextClick}
                      size="small"
                    />
                  </Tooltip>
                  <MenuItem onClick={toggleOpenShare} button>
                    <ListItemIcon>
                      <Share fontSize="small" />
                    </ListItemIcon>
                    <span className="capitalize">{t("actions.share")}</span>
                  </MenuItem>
                </>
              )}
              {isLogged && section === PresetSection.FAVORITE && (
                <MenuItem onClick={handleRemoveFromFavorites} button>
                  <ListItemIcon>
                    <DeleteOutline fontSize="small" />
                  </ListItemIcon>
                  <span>{t("menu.remove")}</span>
                </MenuItem>
              )}
              {isLogged && section === PresetSection.LOCAL && (
                <div>
                  <MenuItem onClick={handleAddToFavorites} button>
                    <ListItemIcon>
                      <BookmarkBorder fontSize="small" />
                    </ListItemIcon>
                    <span>{t("menu.add")}</span>
                  </MenuItem>
                  <MenuItem onClick={handleRemoveFromLocals} button>
                    <ListItemIcon>
                      <DeleteOutline fontSize="small" />
                    </ListItemIcon>
                    <span className="capitalize">{t("actions.remove")}</span>
                  </MenuItem>
                </div>
              )}
              {isLogged && section === PresetSection.POPULAR && (
                <div>
                  <MenuItem onClick={handleAddToFavorites} button>
                    <ListItemIcon>
                      <BookmarkBorder fontSize="small" />
                    </ListItemIcon>
                    <span>{t("menu.add")}</span>
                  </MenuItem>
                </div>
              )}
            </div>
            <div className="preset-infos">
              {course && <CourseListContents course={course} />}
              {refName && <div className="ref-name">{refName}</div>}
            </div>
          </MenuList>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};
PresetMenu.propTypes = {
  // from parent
  anchorEl: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  preset: PropTypes.object,
  section: PropTypes.number,
  // data props
  isLogged: PropTypes.bool.isRequired,
  // dispatch props
  manageAddToFavorites: PropTypes.func.isRequired,
  manageRemoveFromFavoritesWithId: PropTypes.func.isRequired,
  manageRemoveFromLocalsWithId: PropTypes.func.isRequired,
};

PresetMenu.defaultProps = {
  anchorEl: null,
  preset: null,
  section: null,
};

export default PresetMenu;
